/**
 * A map of how bumpy the roads are, built from our own drives.
 *
 * Every trip records a roughness index per sample (see roughness.ts). Once
 * enough drives exist, that becomes a picture of which roads punish the load -
 * knowledge no map sells and no routing engine has. It is used three ways:
 * as a map overlay, as a warning when choosing between routes, and as a live
 * heads-up before a bad stretch arrives.
 *
 * Lookups have to be cheap: this runs against a candidate route on every
 * re-route and against the vehicle position while driving. Samples are binned
 * into a coarse spatial hash so a query touches a handful of cells rather than
 * every point ever recorded.
 */

import type { LngLat, Route, Trip } from '../core/types';
import { cumulativeDistances, distance } from '../core/geo';
import { readPoint } from './tripRecorder';

/** Grid cell size in degrees; ~220 m at Thai latitudes. */
const CELL = 0.002;
/** A route point counts as "on" a recorded sample within this distance. */
const MATCH_M = 60;
/** Below this index nothing is worth storing at all, at any sensitivity. */
const STORE_FLOOR = 25;

/**
 * What counts as "rough", per sensitivity setting (1 = only broken roads,
 * 5 = mention anything). The driver's point: rough IS normal here, so a fixed
 * threshold either cries wolf on every back road or never speaks.
 */
const SENSITIVITY_INDEX: Record<number, number> = { 1: 62, 2: 52, 3: 44, 4: 37, 5: 30 };

export function roughThreshold(sensitivity: number): number {
  return SENSITIVITY_INDEX[sensitivity] ?? SENSITIVITY_INDEX[2];
}

/**
 * A bad road is a LENGTH of bad road, not a bad moment.
 *
 * "the roughness should not be on single pot holes, more a continuous
 * roughness for more than 1km. or a lot of pot holes during a 3km stretch."
 * So there are two ways to qualify: a continuous rough run of at least
 * CONTINUOUS_M, or enough separate rough patches inside a DENSITY_WINDOW_M
 * window that the whole stretch is punishing even though it is not uniformly
 * broken. A single pothole qualifies as neither.
 */
const CONTINUOUS_M = 1000;
const DENSITY_WINDOW_M = 3000;
/** Rough metres needed inside that window for it to count. */
const DENSITY_ROUGH_M = 700;
/** Gap that still counts as the same stretch. */
const JOIN_GAP_M = 400;

export interface RoughSample {
  at: LngLat;
  index: number;
}

export interface RoughIndex {
  cells: Map<string, RoughSample[]>;
  count: number;
}

function key(lng: number, lat: number): string {
  return `${Math.floor(lng / CELL)}:${Math.floor(lat / CELL)}`;
}

/** Build the lookup from stored trips. Cheap enough to run on boot. */
export function buildRoughIndex(trips: Trip[]): RoughIndex {
  const cells = new Map<string, RoughSample[]>();
  let count = 0;

  for (const trip of trips) {
    for (const raw of trip.track ?? []) {
      const p = readPoint(raw);
      const index = p[6];
      if (index < STORE_FLOOR) continue; // only remember the bad bits
      const k = key(p[0], p[1]);
      const list = cells.get(k) ?? [];
      // One sample per cell is plenty; keep the worst seen there.
      const existing = list[0];
      if (existing) {
        if (index > existing.index) existing.index = index;
      } else {
        list.push({ at: [p[0], p[1]], index });
        cells.set(k, list);
        count++;
      }
    }
  }
  return { cells, count };
}

/** Worst recorded roughness near a point, or 0 if nothing is known there. */
export function roughnessAt(index: RoughIndex, at: LngLat): number {
  if (!index.count) return 0;
  let worst = 0;
  const cx = Math.floor(at[0] / CELL);
  const cy = Math.floor(at[1] / CELL);
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (const s of index.cells.get(`${cx + dx}:${cy + dy}`) ?? []) {
        if (distance(at, s.at) <= MATCH_M && s.index > worst) worst = s.index;
      }
    }
  }
  return worst;
}

export interface RoughStretch {
  /** Metres along the route where the bad surface starts / ends. */
  from: number;
  to: number;
  /** Worst index recorded in it. */
  index: number;
}

export interface RouteRoughness {
  /** Metres of this route we know to be rough. */
  metres: number;
  /** Worst index anywhere on the route. */
  worst: number;
  stretches: RoughStretch[];
  /** True when we have driven enough of this route to say anything. */
  known: boolean;
}

/**
 * How much of a route runs over surfaces we already know to be bad.
 * Returns `known: false` when we simply have not driven this way.
 */
export function roughnessOfRoute(
  route: Route,
  index: RoughIndex,
  sensitivity = 2,
): RouteRoughness {
  const empty: RouteRoughness = { metres: 0, worst: 0, stretches: [], known: false };
  if (!index.count) return empty;

  const threshold = roughThreshold(sensitivity);
  const coords = route.geometry.coordinates;
  const cum = cumulativeDistances(coords);
  const raw: RoughStretch[] = [];
  let current: RoughStretch | null = null;
  let matched = 0;

  // Every ~100 m is enough resolution and keeps long routes fast.
  const step = Math.max(1, Math.floor(coords.length / 800));
  for (let i = 0; i < coords.length; i += step) {
    const value = roughnessAt(index, coords[i]);
    if (value > 0) matched++;
    if (value >= threshold) {
      if (current && cum[i] - current.to < JOIN_GAP_M) {
        current.to = cum[i];
        current.index = Math.max(current.index, value);
      } else {
        if (current) raw.push(current);
        current = { from: cum[i], to: cum[i], index: value };
      }
    }
  }
  if (current) raw.push(current);

  /*
   * Qualify each stretch two ways. Either it is long enough on its own, or it
   * sits inside a window with enough other rough metres around it that the
   * whole stretch is a bad drive. Everything else is a pothole, and a pothole
   * is not a road condition.
   */
  const real = raw.filter((st) => {
    if (st.to - st.from >= CONTINUOUS_M) return true;
    const from = st.from - DENSITY_WINDOW_M / 2;
    const to = st.to + DENSITY_WINDOW_M / 2;
    let inWindow = 0;
    for (const other of raw) {
      if (other.to < from || other.from > to) continue;
      inWindow += Math.min(other.to, to) - Math.max(other.from, from);
    }
    return inWindow >= DENSITY_ROUGH_M;
  });

  const metres = real.reduce((sum, st) => sum + (st.to - st.from), 0);
  const worst = real.reduce((w, st) => Math.max(w, st.index), 0);

  return {
    metres: Math.round(metres),
    worst,
    stretches: real.sort((a, b) => b.index - a.index).slice(0, 5),
    // If almost none of the route matched anything we've driven, stay quiet
    // rather than implying the road is fine.
    known: matched > 3,
  };
}

/**
 * Penalty in seconds to add to a route's effective cost for its rough sections.
 *
 * The driver asked for rough roads to actually deter a route rather than only
 * annotate it. Charged per kilometre and scaled by how bad the surface is, so
 * a short patch is a nudge and forty kilometres of broken tarmac is decisive.
 * It is applied to route RANKING only, never to the ETA shown - the journey
 * does not take longer because we dislike the road.
 */
export function roughPenaltySeconds(roughness: RouteRoughness | null): number {
  if (!roughness?.known || !roughness.metres) return 0;
  const km = roughness.metres / 1000;
  // 60 s/km at the threshold, rising to ~150 s/km on genuinely broken surfaces.
  const severity = Math.max(1, Math.min(2.5, roughness.worst / 45));
  return Math.round(km * 60 * severity);
}

/** The next known-rough stretch in front of the vehicle, for a live warning. */
export function roughAhead(
  roughness: RouteRoughness,
  along: number,
  lookaheadM: number,
): RoughStretch | null {
  let best: RoughStretch | null = null;
  for (const s of roughness.stretches) {
    if (s.to < along) continue; // behind us
    const lead = s.from - along;
    if (lead < -50 || lead > lookaheadM) continue;
    if (!best || s.from < best.from) best = s;
  }
  return best;
}
